import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  DialogTitle,
  IconButton,
  Stepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
  useTheme,
  StepLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import CompactClientWizardStepper from './CompactClientWizardStepper';

const PREFIX = 'ClientWizardHeader';

const classes = {
  header: `${PREFIX}-header`,
  htop: `${PREFIX}-htop`,
  hbottom: `${PREFIX}-hbottom`,
  title: `${PREFIX}-title`,
  stepper: `${PREFIX}-stepper`,
  steps: `${PREFIX}-steps`,
  ofstep: `${PREFIX}-ofstep`,
  closeButton: `${PREFIX}-closeButton`,
  clientNameTitle: `${PREFIX}-clientNameTitle`,
  compactStepperContainer: `${PREFIX}-compactStepperContainer`,
};

const Root = styled(DialogTitle)(({ theme }) => ({
  [`&.${classes.header}`]: {
    backgroundColor: theme.palette.lightActionDisabled04p.main,
    padding: theme.spacing(6, 18, 3, 18),
    position: 'relative',
  },

  [`& .${classes.htop}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  [`& .${classes.hbottom}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    marginTop: -12,
    fontFamily: 'Bitter',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    color: theme.palette.lightTextPrimary.main,
    fontSize: 36,
    fontWeight: 700,
    lineHeight: '48px',
    letterSpacing: '0.5px',
    margin: 'auto 0',
  },

  [`& .${classes.clientNameTitle}`]: {
    color: theme.palette.lightActionDisabled90p.main,
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  [`& .${classes.stepper}`]: {
    backgroundColor: '#fff',
    // width: 304,
    '& .MuiSvgIcon-root:not(.Mui-completed):not(.Mui-active) circle': {
      fill: theme.palette.lightActionDisabled45p?.main,
    },
  },

  [`& .${classes.steps}`]: {
    display: 'flex',
    alignSelf: 'center',
  },

  [`& .${classes.ofstep}`]: {
    fontWeight: 'normal',
  },

  [`& .${classes.closeButton}`]: {
    position: 'absolute',
    right: theme.spacing(0.375),
    top: theme.spacing(0.375),
  },

  [`& .${classes.compactStepperContainer}`]: {
    paddingBottom: 20,
  },
}));

interface ClientWizardHeaderProps {
  passengerName: string;
  title: string;
  steps: string[];
  activeStep: number;
  isValid: boolean;
  step: (step: number) => () => void;
  close: (saved: boolean) => void;
  displayCompactStepper?: boolean;
}

export default function ClientWizardHeader({
  passengerName,
  title,
  steps,
  activeStep,
  isValid,
  step,
  close,
  displayCompactStepper,
}: ClientWizardHeaderProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = displayCompactStepper
    ? false
    : useMediaQuery(theme.breakpoints.up('md'));

  const handleClose = () => {
    if (close) {
      close(false);
    }
  };

  return (
    <Root className={classes.header}>
      <Box className={classes.htop}>
        <Typography variant="h6" className={classes.clientNameTitle}>
          {passengerName}
        </Typography>
        <IconButton
          onClick={handleClose}
          className={classes.closeButton}
          aria-label="close"
          data-testid="close"
          size="large">
          <CloseIcon sx={{ fontSize: 35 }} />
        </IconButton>
      </Box>
      <Box className={classes.hbottom}>
        <Typography variant="h4" className={classes.title}>
          {title}
        </Typography>
        {isLarge && (
          <Stepper
            nonLinear
            activeStep={activeStep}
            className={classes.stepper}>
            {steps.map((label, index) => (
              <Step key={index} completed={activeStep > index}>
                {/* <StepButton
                  color="inherit"
                  disabled={index > activeStep}
                  onClick={step(index)}>
                  {label}
                </StepButton> */}
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {!isLarge && (
          <Box className={classes.compactStepperContainer}>
            <CompactClientWizardStepper
              activeStep={activeStep + 1}
              totalStepCount={steps.length}
            />
          </Box>
        )}
      </Box>
    </Root>
  );
}
